/**
 * Which agent a session runs on (Claude Code, Codex, ...), always as icon plus label.
 * Unknown kinds from newer agents show their own name with a generic icon.
 */
import { agentKindLabel } from '@orgus/protocol';
import type { AgentKind } from '@orgus/protocol';
import { Bird, Bot, Network } from '@orgus/ui/icons';
import { siClaude, siCline, siCursor, siGithubcopilot, siGooglegemini, siGooglejules, siJetbrains, siKimi, siOpencode, siQwen } from 'simple-icons';
import type { LucideProps } from '@orgus/ui/icons';
import type { ComponentType } from 'react';
import { Badge, cn } from '@orgus/ui';

type Glyph = ComponentType<{ className?: string }>;

/** Codex: a knot of three rounded loops. */
function CodexGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} className={className} aria-hidden>
      {[0, 60, 120].map((r) => (
        <rect key={r} x="8.25" y="3" width="7.5" height="18" rx="3.75" transform={`rotate(${r} 12 12)`} />
      ))}
    </svg>
  );
}

const lucide = (I: ComponentType<LucideProps>): Glyph => ({ className }) => <I className={className} aria-hidden />;

/** An agent's own logo (simple-icons, CC0), filled with the tint: shape from the brand, colour from Orgus's soft hues. */
const brand =
  (icon: { path: string; title: string }): Glyph =>
  ({ className }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d={icon.path} />
    </svg>
  );

/**
 * Each agent's glyph and its colour (a soft data hue, never a status colour). Real logos where
 * a free one exists; Codex (no free OpenAI mark), goose and Devin keep a drawn glyph.
 */
const AGENTS: Record<string, { glyph: Glyph; tint: string }> = {
  'claude-code': { glyph: brand(siClaude), tint: 'text-data-2' },
  codex: { glyph: CodexGlyph, tint: 'text-data-3' },
  'codex-acp': { glyph: CodexGlyph, tint: 'text-data-3' },
  'codex-cloud': { glyph: CodexGlyph, tint: 'text-data-3' },
  'gemini-cli': { glyph: brand(siGooglegemini), tint: 'text-data-1' },
  jules: { glyph: brand(siGooglejules), tint: 'text-data-8' },
  'copilot-cli': { glyph: brand(siGithubcopilot), tint: 'text-data-8' },
  'copilot-agent': { glyph: brand(siGithubcopilot), tint: 'text-data-8' },
  'cursor-cli': { glyph: brand(siCursor), tint: 'text-fg-muted' },
  'cursor-cloud': { glyph: brand(siCursor), tint: 'text-fg-muted' },
  opencode: { glyph: brand(siOpencode), tint: 'text-data-5' },
  'qwen-code': { glyph: brand(siQwen), tint: 'text-data-4' },
  goose: { glyph: lucide(Bird), tint: 'text-data-7' },
  kimi: { glyph: brand(siKimi), tint: 'text-data-6' },
  cline: { glyph: brand(siCline), tint: 'text-data-5' },
  junie: { glyph: brand(siJetbrains), tint: 'text-data-6' },
  devin: { glyph: lucide(Bot), tint: 'text-data-1' },
  a2a: { glyph: lucide(Network), tint: 'text-fg-muted' },
};

const agentOf = (kind: AgentKind | null | undefined) => AGENTS[kind || 'claude-code'] ?? { glyph: lucide(Bot), tint: 'text-fg-muted' };

/** The agent's own logo or glyph, in its colour: Claude's mark in coral, Codex a knot, Gemini's sparkle. */
export function AgentGlyph({ kind, className }: { kind: AgentKind | null | undefined; className?: string }) {
  const a = agentOf(kind);
  const G = a.glyph;
  return <G className={cn(a.tint, className)} />;
}

/** The agent's own colour as a text class (text-data-2 for Claude), for its ripple and accents. */
export function agentHue(kind: AgentKind | null | undefined): string {
  return agentOf(kind).tint;
}

/** Kept for callers that want a component: the glyph for this kind. */
export function agentIcon(kind: AgentKind | null | undefined): Glyph {
  const a = agentOf(kind);
  const G = a.glyph;
  return ({ className }) => <G className={cn(a.tint, className)} />;
}

/** Icon and label, inline with text (rows, facts, menus). */
export function AgentKindLabel({ kind, className, iconOnlyBelow }: { kind: AgentKind | null | undefined; className?: string; iconOnlyBelow?: 'sm' | 'md' }) {
  const Icon = agentIcon(kind);
  const label = agentKindLabel(kind);
  return (
    <span className={cn('inline-flex min-w-0 shrink-0 items-center gap-1', className)} data-agent-kind={kind || 'claude-code'}>
      <Icon className="size-3 shrink-0" aria-hidden />
      <span className={cn('truncate', iconOnlyBelow === 'sm' && 'sr-only sm:not-sr-only', iconOnlyBelow === 'md' && 'sr-only md:not-sr-only')}>{label}</span>
    </span>
  );
}

/** As a badge (drawer header, dialogs). */
export function AgentKindBadge({ kind, variant = 'outline' }: { kind: AgentKind | null | undefined; variant?: 'soft' | 'outline' }) {
  const Icon = agentIcon(kind);
  return (
    <Badge tone="neutral" variant={variant} icon={<Icon />} data-agent-kind={kind || 'claude-code'}>
      {agentKindLabel(kind)}
    </Badge>
  );
}
